import { useEffect, useEffectEvent, useState, type CSSProperties } from "react";
import type { Figure } from "@softmax/avatar/character";
import { VILLAGER, type Look } from "@softmax/avatar/wardrobe";
import type { CogentWidgetProps } from "./widget-types";
export function useWidgetCharacter(props: CogentWidgetProps) {
  const [look, setLook] = useState<Look | null | undefined>(undefined);
  const [drawn, setDrawn] = useState<"loading" | "ready" | "failed">("loading");
  const [attempt, setAttempt] = useState(0);
  const [figure, setFigure] = useState<Figure | null>(null);
  const [settled, setSettled] = useState(false);
  const loadLook = useEffectEvent(() => props.adapter.loadLook());
  useEffect(() => {
    let current = true;
    void loadLook().then(
      (next) => {
        if (current) setLook(next);
      },
      (error: unknown) => {
        if (!current) return;

        console.error("cogent: reading its look failed", error);
        setLook(null);
      },
    );
    return () => {
      current = false;
    };
  }, []);
  useEffect(() => {
    let idle = 0;
    let timer = 0;
    const loaded = () => {
      const whenIdle = window.requestIdleCallback as
        | typeof window.requestIdleCallback
        | undefined;

      if (whenIdle) idle = whenIdle(() => setSettled(true), { timeout: 2000 });
      else timer = window.setTimeout(() => setSettled(true), 200);
    };

    if (document.readyState === "complete") loaded();
    else window.addEventListener("load", loaded, { once: true });

    return () => {
      window.removeEventListener("load", loaded);

      if (idle) window.cancelIdleCallback(idle);

      window.clearTimeout(timer);
    };
  }, []);
  const retry = () => {
    setDrawn("loading");
    setAttempt((current) => current + 1);
  };
  const onFigure = (next: Figure) =>
    setFigure((current) =>
      JSON.stringify(current) === JSON.stringify(next) ? current : next,
    );
  const wearing = props.cogent ? look : VILLAGER;
  const style: CSSProperties = figure
    ? ({
        "--fig-left": `${figure.left}px`,
        "--fig-top": `${figure.top}px`,
        "--fig-right": `${figure.right}px`,
        "--fig-bottom": `${figure.bottom}px`,
        "--head-left": `${figure.head.left}px`,
        "--head-top": `${figure.head.top}px`,
        "--head-right": `${figure.head.right}px`,
        "--head-bottom": `${figure.head.bottom}px`,
        "--mouth-y": `${figure.mouthY}px`,
      } as CSSProperties)
    : {};
  return {
    drawn,
    setDrawn,
    attempt,
    settled,
    wearing,
    style,
    retry,
    onFigure,
  };
}
